import React, { useCallback } from 'react';

export interface ContentSelectContextValue {
  selectedContents: string[];
  toggleSelectContent: (contentId: string) => void;
  selectContents: (contentIds: string[]) => void;
  resetSelectedContents: () => void;
}

export const ContentSelectContext: React.Context<ContentSelectContextValue | null> =
  React.createContext<ContentSelectContextValue | null>(null);

export const useContentSelect = (): ContentSelectContextValue => {
  const context = React.useContext(ContentSelectContext);
  if (!context) {
    throw new Error(
      'useContentSelect must be used within a ContentSelectProvider',
    );
  }
  return context;
};

export const ContentSelectProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [selectedContents, setSelectedContents] = React.useState<string[]>([]);

  const toggleSelectContent = useCallback((contentId: string) => {
    setSelectedContents((prev) =>
      prev.includes(contentId)
        ? prev.filter((id) => id !== contentId)
        : [...prev, contentId],
    );
  }, []);

  const selectContents = useCallback((contentIds: string[]) => {
    setSelectedContents(contentIds);
  }, []);

  const resetSelectedContents = useCallback(() => {
    setSelectedContents([]);
  }, []);

  const value = React.useMemo(
    () => ({
      selectedContents,
      toggleSelectContent,
      selectContents,
      resetSelectedContents,
    }),
    [
      selectedContents,
      toggleSelectContent,
      selectContents,
      resetSelectedContents,
    ],
  );

  return (
    <ContentSelectContext.Provider value={value}>
      {children}
    </ContentSelectContext.Provider>
  );
};
